import { lazy, type ComponentType } from "react";

/**
 * After a deploy, a tab that was already open still references the previous
 * build's hashed chunks (`/assets/Dashboard-<oldhash>.js`). Those files are
 * gone, so opening another page fails to load its chunk. Reloading once picks
 * up the new build; the guard in sessionStorage keeps a genuinely broken chunk
 * from looping.
 */

const RELOAD_KEY = "zettl:chunk-reload-at";
/** A second failure within this window is a real error, not a stale tab. */
export const RELOAD_GUARD_MS = 30_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk [\w-]+ failed|ChunkLoadError/i;

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === "string"
        ? error
        : "";
  return CHUNK_ERROR.test(message);
}

/**
 * Reloads the page unless it already did so within the guard window.
 * Returns whether a reload was started.
 */
export function reloadOnceForChunkError(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (last && now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // No sessionStorage (privacy mode): reload anyway; a loop needs storage to be guarded,
    // but a blocked chunk without storage is rare enough to accept one extra reload.
  }
  window.location.reload();
  return true;
}

/** React.lazy that reloads the page once when the chunk of a newer deploy is missing. */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadOnceForChunkError()) {
        // The page is reloading; never resolve so nothing renders an error meanwhile.
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    })
  );
}

/** Vite fires `vite:preloadError` when a dynamic import's dependency fails to preload. */
export function installChunkReloadHandler(target: Window = window): void {
  target.addEventListener("vite:preloadError", (event) => {
    if (reloadOnceForChunkError()) event.preventDefault();
  });
}
